import React from 'react'

import { useConnect } from 'redux-bundler-hook'

import { Typography } from '@mui/material'

import { EMPTY_OBJECT, formattedDate } from '~/src/Lib/Utils'
import PestIcon from '~/src/UI/Icons/Pest'
import BaseWidget from '~/src/UI/Widgets/Base'
import { WidgetPesticide as PesticideWidget } from '~/src/UI/Widgets/Pesticide'

import Base from './Base'

export const formatPesticideApplication = dataToFormat => {
  const {
    annotation: {
      applicationRate,
      pesticide: annotationPesticide,
      targetPest,
    },
    entities,
    user,
  } = dataToFormat
  const { pesticides = EMPTY_OBJECT } = entities

  let pesticide = annotationPesticide
  if (typeof pesticide === 'number') {
    pesticide = pesticides[pesticide]
  }

  if (pesticide) {
    const { activeIngredient } = pesticide

    return {
      title: activeIngredient,
      body: [applicationRate, targetPest].filter(Boolean).join(' \u2013 '),
      user,
    }
  }

  return {
    title: '',
    body: [applicationRate, targetPest].filter(Boolean).join(' \u2013 '),
    user,
  }
}

export const Formatter = props => {
  const {
    annotation: {
      applicationRate,
      pesticide: annotationPesticide,
      targetPest,
    },
    entities: { pesticides },
    user,
    tooltip,
    ...passthruProps
  } = props

  let pesticide = annotationPesticide
  if (typeof pesticide === 'number') {
    pesticide = pesticides[pesticide]
  }
  const { activeIngredient } = pesticide

  return (
    <Base
      {...passthruProps}
      title={(
        <Typography variant="body1" color="textPrimary">
          {pesticide?.name ?? pesticides[pesticide]?.name}
          {activeIngredient ? (
            <small>
              &nbsp;({activeIngredient})
            </small>
          ) : null}
        </Typography>
      )}
      body={[applicationRate, targetPest].filter(Boolean).join(' \u2013 ')}
      user={user}
    />
  )
}
Formatter.displayName = 'Annotatation/PesticideApplication'

export const SummaryComponent = props => {
  const { id } = props
  const { entities } = useConnect('selectEntities')
  const {
    facilityMemberships,
    pesticideApplications,
    pesticides,
    users
  } = entities
  const pesticideApplication = pesticideApplications[id]
  const pesticide = pesticides[pesticideApplication?.pesticide] ?? pesticideApplication?.pesticide

  if (!pesticideApplication) {
    console.warn('no pesticide application found!', { id, pesticideApplications })
    return null
  }

  const { applicationRate, appliedBy, createdBy, targetPest, ts } = pesticideApplication
  let user = appliedBy
    ? users?.[facilityMemberships?.[appliedBy]?.user]
    : null
  if (!user) {
    user = users[createdBy]
  }

  const summary = [
    user && user.fullName,
    applicationRate ? ` applied ${applicationRate} of` : ' applied',
    pesticide && ` ${pesticide.name} (${pesticide.activeIngredient})`,
    targetPest && ` to control ${targetPest}`,
    ts && ` on ${formattedDate(ts, 'SHORT_DATE')}`,
  ]

  return `${summary.filter(Boolean).join('').replace(/^\w/, first => first.toUpperCase())}.`
}
SummaryComponent.displayName = 'IPM/ApplicationSummary'
export const Summary = React.memo(SummaryComponent)

export const Widget = React.memo(props => {
  const { id } = props
  const { entities } = useConnect('selectEntities')
  const {
    facilityMemberships,
    pesticideApplications,
    pesticides,
    users
  } = entities
  const pesticideApplication = pesticideApplications[id]
  const pesticide = pesticides[pesticideApplication.pesticide] ?? pesticideApplication.pesticide

  if (!pesticideApplication) return null

  const { appliedBy, createdBy } = pesticideApplication
  let user = appliedBy ? users?.[facilityMemberships?.[appliedBy]?.user] : null
  if (!user) {
    user = users[createdBy]
  }

  return (
    <>
      <PesticideWidget pesticide={pesticide} />
      {pesticideApplication.applicationRate ? (
        <BaseWidget
          description="application rate"
          title={pesticideApplication.applicationRate}
        />
      ) : null}
      {pesticideApplication.targetPest ? (
        <BaseWidget
          description="target pest"
          title={pesticideApplication.targetPest}
          Icon={PestIcon}
        />
      ) : null}
    </>
  )
})

export default React.memo(props => {
  const {
    entities,
  } = useConnect('selectEntities')
  const { facilityMemberships, users } = entities
  let user = props.user
  if (!user) {
    const { annotation: { appliedBy, createdBy } } = props
    user = appliedBy && facilityMemberships ? users[facilityMemberships[appliedBy]?.user] : null
    if (!user) {
      user = users[createdBy]
    }
  }
  return <Formatter {...props} entities={entities} user={user} />
})
